import { ChevronRight, MapPin } from 'lucide-react';
import styled, { css } from 'styled-components';
import { key, palette } from 'styled-theme';

const desktopBreakpoint = key('breakpoints.desktop', '1024px');
const tabletBreakpoint = key('breakpoints.tablet', '820px');

const gap = {
  tiny: key('sizes.gap.tiny', '4px'),
  small: key('sizes.gap.small', '8px'),
  normal: key('sizes.gap.normal', '16px'),
  medium: key('sizes.gap.medium', '24px'),
  big: key('sizes.gap.big', '32px'),
};

const radius = {
  tiny: key('sizes.borderRadius.tiny', '4px'),
  small: key('sizes.borderRadius.small', '8px'),
  normal: key('sizes.borderRadius.normal', '12px'),
};

const font = {
  small: key('sizes.font.small', '12px'),
  normal: key('sizes.font.normal', '14px'),
  medium: key('sizes.font.medium', '16px'),
  header2: key('sizes.font.header2', '20px'),
  header1: key('sizes.font.header1', '26px'),
};

const accent = palette('color', 0, '#7b5cff');

export const ApplicationCardWrapper = styled.article`
  background-color: white;
  border-radius: ${radius.tiny};
  margin-bottom: ${gap.medium};
  position: relative;
  overflow: hidden;
  padding: ${gap.medium} ${gap.medium} ${gap.normal};
  cursor: pointer;

  &:focus-within {
    outline: 2px solid ${accent};
    outline-offset: 2px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${accent};
  }

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.normal} ${gap.normal} ${gap.small};
    margin-bottom: ${gap.normal};
  }
`;

export const ProcedureName = styled.h2`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 ${gap.small} 0;
`;

export const ProcedureId = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.normal};
  color: #6b7280;
  display: block;
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gap.normal};
`;

export const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AddressLabel = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  font-weight: 500;
  color: #1f2937;
  margin-bottom: ${gap.tiny};
`;

export const AddressText = styled.address`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  font-style: normal;
  color: #4b5563;
  line-height: 1.5;
`;

export const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DescriptionLabel = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  color: #1f2937;
  line-height: 1.5;
  margin: 0;
`;

export const DescriptionText = styled.p`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.normal};
  font-weight: 500;
  color: #4b5563;
  margin-bottom: ${gap.tiny};
`;

/* Navbar-driven — left untouched, handled separately */
export const Main = styled.main`
  margin-top: 95px;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

export const ApplicationContainer = styled.div`
  width: 84%;
  margin: auto;

  @media (max-width: ${desktopBreakpoint}) {
    width: 90%;
  }

  @media (max-width: ${tabletBreakpoint}) {
    width: 100%;
    margin: 0;
    padding: 0 ${gap.normal};
  }
`;

export const Container = styled.div`
  padding: ${gap.small} ${gap.normal};

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.small} 0;
  }
`;

export const BackButton = styled.button`
  cursor: pointer;
  margin-bottom: ${gap.normal};
  background: transparent;
  color: #2563eb;
  border: none;
  font-size: ${font.medium};
  font-weight: 500;
  display: flex;
  align-items: center;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  padding: ${gap.tiny} ${gap.small} ${gap.tiny} 0;
  border: 1px solid #2563eb;
  border-radius: ${radius.small};

  &:hover {
    color: #1d4ed8;
  }
`;

export const Card = styled.article`
  background-color: white;
  border-radius: ${radius.tiny};
  padding: ${gap.big};
  border: 1px solid ${palette('border', 1, '#6366f1')};

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.medium};
  }
`;

export const Header = styled.header`
  margin-bottom: ${gap.big};
  border-bottom: 1px solid ${palette('border', 0, '#D7D7E2')};
  padding-bottom: ${gap.medium};

  @media (max-width: ${tabletBreakpoint}) {
    margin-bottom: ${gap.medium};
    padding-bottom: ${gap.normal};
  }
`;

export const Title = styled.h1`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.header1};
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 ${gap.small} 0;
`;

export const Section = styled.section`
  margin-bottom: ${gap.medium};
`;

export const SectionTitle = styled.h2`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  font-weight: 500;
  color: #4b5563;
  margin: 0 0 ${gap.normal} 0;
`;

export const DetailItem = styled.div`
  margin-bottom: ${gap.medium};
`;

export const DetailLabel = styled.span`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.normal};
  font-weight: 500;
  color: #6b7280;
  display: block;
  margin-bottom: ${gap.tiny};
`;

export const DetailValue = styled.p`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  color: #1f2937;
  margin: 0;
  line-height: 1.5;
`;

export const Address = styled(DetailValue)`
  font-style: normal;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: ${gap.tiny} ${gap.small};
  border-radius: ${radius.tiny};
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.small};
  font-weight: 500;
  /* Status colors — no semantic success/warning/error tokens in the theme yet */
  background-color: ${(props) =>
    props.status === 'approved'
      ? '#10B981'
      : props.status === 'pending'
        ? '#F59E0B'
        : props.status === 'rejected'
          ? '#EF4444'
          : '#6B7280'};
  color: white;
  margin-left: ${gap.normal};

  @media (max-width: ${tabletBreakpoint}) {
    margin-left: ${gap.small};
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

export const ErrorContainer = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: ${radius.small};
  padding: ${gap.normal};
  margin: ${gap.big} 0;
  color: #b91c1c;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${gap.normal};

  @media (max-width: ${tabletBreakpoint}) {
    flex-wrap: wrap;
    gap: ${gap.small};
  }
`;

export const TagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${gap.small};
  flex-wrap: wrap;
`;

export const ProjectTypeTag = styled.div`
  font-size: ${font.normal};
  font-weight: 500;
  /* Tag colors — no theme token, see note at top of file */
  color: #0369a1;
  background-color: #e0f2fe;
  padding: ${gap.tiny} ${gap.small};
  border-radius: ${radius.tiny};
`;

export const ApplicationTypeTag = styled.div`
  font-size: ${font.normal};
  font-weight: 500;
  color: ${(props) => props.color || '#2E7D32'};
  background-color: ${(props) => props.backgroundColor || '#E8F5E9'};
  padding: ${gap.tiny} ${gap.small};
  border-radius: ${radius.tiny};
`;

export const ApplicationId = styled.div`
  font-size: ${font.normal};
  color: #6b7280;
`;

export const AddressRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${gap.small};
  font-weight: 500;
  font-size: ${font.medium};
  color: #1f2937;

  @media (max-width: ${tabletBreakpoint}) {
    font-size: ${font.normal};
  }
`;

export const LocationIcon = styled(MapPin)`
  color: #9ca3af;
  margin-right: ${gap.small};
  flex-shrink: 0;
  margin-top: ${gap.tiny};
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${gap.medium};

  @media (max-width: ${tabletBreakpoint}) {
    margin-top: ${gap.normal};
  }
`;

export const SubmittedDate = styled.div`
  font-size: ${font.normal};
  color: #6b7280;
`;

export const ViewDetailsButton = styled.button`
  background: transparent;
  color: #2563eb;
  border: none;
  font-size: ${font.normal};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  padding: 0;

  &:hover {
    color: #1d4ed8;
  }
`;

export const ChevronIcon = styled(ChevronRight)`
  margin-left: 2px; /* micro icon nudge, kept literal — not a spacing-scale value */
`;

export const EmptyState = styled.div`
  padding: ${gap.normal} 0;
  text-align: center;
`;

export const FlexLayout = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: ${gap.normal};

  @media (max-width: ${tabletBreakpoint}) {
    grid-template-columns: 1fr;
    gap: ${gap.normal};
  }
`;

export const OverlaysCard = styled.div`
  background: #fff;
  border-radius: ${radius.tiny};
  padding: ${gap.medium};
  height: fit-content;

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.normal};
  }
`;

export const OverlayList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const OverlayItem = styled.li`
  padding: ${gap.small} 0;
  border-bottom: 1px solid ${palette('border', 0, '#eee')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${gap.small};

  &:last-child {
    border-bottom: none;
  }
`;

export const OverlayName = styled.span`
  font-size: ${font.normal};
  font-weight: 500;
`;

export const ViewDocumentLink = styled.button`
  /* Tag/link colors — no theme token, see note at top of file */
  background: #e6f7ff;
  color: #1890ff;
  padding: ${gap.tiny} ${gap.small};
  border-radius: ${radius.tiny};
  font-size: ${font.small};
  text-decoration: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  gap: ${gap.tiny};
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  white-space: nowrap;

  &:hover {
    background: #bae7ff;
  }
`;

export const StatusTag = styled.span`
  background: #f5f5f5;
  color: #666;
  padding: ${gap.tiny} ${gap.small};
  border-radius: ${radius.tiny};
  font-size: ${font.small};
  white-space: nowrap;
`;

export const ReportItem = styled.div`
  padding: ${gap.small} 0;
  border-bottom: 1px solid ${palette('border', 0, '#eee')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${gap.small};

  ${(props) =>
    props.isLast &&
    css`
      border-bottom: none;
      margin-bottom: ${gap.small};
    `}
`;

export const UploadButton = styled.a`
  /* Tag/link colors — no theme token, see note at top of file */
  background: #e8f9d8;
  color: #52c41a;
  padding: ${gap.tiny} ${gap.small};
  border-radius: ${radius.tiny};
  font-size: ${font.small};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${gap.tiny};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #d9f7be;
  }
`;

export const WelcomeContainer = styled.div`
  padding: ${gap.normal} 0;
  margin-bottom: ${gap.normal};
`;

export const WelcomeHeading = styled.h1`
  /* Theme's largest font token (header1, 26px) is smaller than the
     original 2rem/32px hero size — add a "hero"/"display" token to
     theme.sizes.font if you want the full 32px preserved here. */
  font-size: ${font.header1};
  font-weight: 600;
  color: #1f1f1f;
  margin: 0;
  padding: 0;
  margin-bottom: ${gap.small};

  @media (max-width: ${tabletBreakpoint}) {
    font-size: ${font.medium};
  }
`;

export const WelcomeSubtext = styled.p`
  font-size: ${font.header2};
  color: #555;
  margin: 0;
  padding: 0;

  @media (max-width: ${tabletBreakpoint}) {
    font-size: ${font.medium};
  }
`;

export const TooltipWrapper = styled.div`
  position: relative;
`;

export const TooltipContent = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};
  background-color: #f8f9fa;
  color: #2c3e50;
  text-align: center;
  padding: ${gap.small} ${gap.normal};
  border-radius: ${radius.small};
  position: fixed;
  z-index: 9999;
  line-height: 1.5;
  font-size: ${font.normal};
  pointer-events: none;
  min-width: 300px;
  max-width: 300px;
  white-space: normal;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border: 1px solid ${accent};

  @media (max-width: ${tabletBreakpoint}) {
    min-width: 240px;
    max-width: 80vw;
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -7px;
    border-width: 7px;
    border-style: solid;
    border-color: #f8f9fa transparent transparent transparent;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -8px;
    border-width: 8px;
    border-style: solid;
    border-color: ${accent} transparent transparent transparent;
    z-index: -1;
  }
`;

export const DisabledOverlay = styled.div`
  position: relative;
  opacity: 0.5;
  cursor: not-allowed;

  button {
    cursor: not-allowed;
    pointer-events: auto !important;
  }
`;

export const ConvertButton = styled.button`
  display: flex;
  align-items: center;
  justify-self: flex-end;
  gap: ${gap.small};
  padding: ${gap.small} ${gap.normal};
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  /* Gradient end (#60a5fa) has no matching second accent in the theme yet */
  background: linear-gradient(90deg, ${accent}, #60a5fa);
  color: white;
  border: none;
  border-radius: ${radius.small};
  font-size: ${font.normal};
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.3s,
    transform 0.1s;
  margin-top: ${gap.small};

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    transition: transform 0.3s;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @media (max-width: ${tabletBreakpoint}) {
    justify-self: stretch;
    width: 100%;
    justify-content: center;
  }
`;
